/**
 * FileUpload Component (TASK-017)
 *
 * File upload component with drag-and-drop support for Excel files.
 */
import { useState, useRef, useCallback } from 'react';
import { Upload, X, FileSpreadsheet, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatFileSize } from '@/lib/utils';

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  onError: (error: string) => void;
  disabled?: boolean;
  loading?: boolean;
}

const ACCEPTED_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export function FileUpload({
  onFileSelect,
  onError,
  disabled = false,
  loading = false,
}: FileUploadProps): JSX.Element {
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback(
    (file: File): boolean => {
      // Check file type
      const isValidType = ACCEPTED_TYPES.includes(file.type);
      const hasValidExtension = ACCEPTED_EXTENSIONS.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );

      if (!isValidType && !hasValidExtension) {
        const errorMsg = 'Неверный тип файла. Принимаются только Excel файлы (.xlsx, .xls).';
        setError(errorMsg);
        onError(errorMsg);
        return false;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        const errorMsg = 'Файл слишком большой. Максимальный размер — 50МБ.';
        setError(errorMsg);
        onError(errorMsg);
        return false;
      }

      return true;
    },
    [onError]
  );

  const handleFile = useCallback(
    (file: File) => {
      if (disabled || loading) return;

      setError(null);

      if (validateFile(file)) {
        setSelectedFile(file);
        onFileSelect(file);
      }
    },
    [disabled, loading, onFileSelect, validateFile]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !loading) {
        setIsDragActive(true);
      }
    },
    [disabled, loading]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !loading) {
        setIsDragActive(true);
      }
    },
    [disabled, loading]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      if (disabled || loading) return;

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [disabled, loading, handleFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleClear = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelect(null);
  }, [onFileSelect]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        fileInputRef.current?.click();
      }
    },
    []
  );

  const handleZoneClick = useCallback(() => {
    if (!disabled && !loading) {
      fileInputRef.current?.click();
    }
  }, [disabled, loading]);

  const isDisabled = disabled || loading;

  return (
    <div data-testid="file-upload" className="w-full">
      {loading && (
        <div
          data-testid="upload-loading"
          className="flex items-center justify-center p-8"
        >
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Загрузка...</span>
        </div>
      )}

      {!loading && !selectedFile && (
        <div
          data-testid="upload-zone"
          role="button"
          tabIndex={0}
          className={cn(
            'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
            isDragActive && 'drag-active border-blue-500 bg-blue-50',
            !isDragActive && 'border-gray-300 hover:border-gray-400',
            isDisabled && 'disabled opacity-50 cursor-not-allowed'
          )}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleZoneClick}
          onKeyDown={handleKeyDown}
        >
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600 mb-2">
            <span className="font-medium">Перетащите</span> файл сюда
          </p>
          <p className="text-sm text-gray-500">или нажмите для выбора</p>
          <p className="text-xs text-gray-400 mt-2">
            Допустимые форматы:{' '}
            <span className="font-medium">.xlsx, .xls</span> (макс. 50МБ)
          </p>
        </div>
      )}

      {!loading && selectedFile && (
        <div
          data-testid="selected-file"
          className="border rounded-lg p-4 bg-gray-50"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FileSpreadsheet className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                <p
                  data-testid="file-size"
                  className="text-sm text-gray-500"
                >
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
            </div>
            <button
              data-testid="clear-file-button"
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              aria-label="Удалить файл"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div
          data-testid="file-error"
          role="alert"
          className="mt-2 text-sm text-red-600"
        >
          {error}
        </div>
      )}

      <input
        ref={fileInputRef}
        data-testid="file-input"
        type="file"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        className="hidden"
        onChange={handleInputChange}
        disabled={isDisabled}
        aria-label="Загрузить Excel файл"
      />
    </div>
  );
}

export default FileUpload;
